import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import {
  IRevenueReportItem,
  ILostRevenueData,
  IPeakBookingItem,
  IUpsellContributionItem,
  IUpsellContributionTypeItem,
  IOwnerInsightItem,
  IMarginReport,
  IOwnerReportSummary,
  ILeadReport,
  ICancellationReasonList,
  ICountOfWeeklyGuestEventSummary,
  ICountOfCancellationGuestVisitLostRevenue,
  ICountOfCancellationLostRevenue,
  ICountOfCancellationReasonList,
  ICountOfCancellationList,
  ICountOfGuestVisitList,
  ICountOfGuestVisitChips,
  IEmployeePerformanceReport,
  ICountOfOngoingMoreThan15DaysChips,
  ICountOfDailySummaryChips,
  ICountOfWeeklySummaryChips,
  ICountOfEmployeePerformanceReportChips,
  ICountOfOngoingAging,
  IVenuePerformanceOverview,
  IEventSummary,
  IEventTypeReportItem,
  IRoomReportData,
  ITodayFollowupItem,
} from "./Report.types";

// Revenue Report
export const FETCH_REVENUE_REPORT_PROGRESS = "FETCH_REVENUE_REPORT_PROGRESS";
export const FETCH_REVENUE_REPORT_SUCCESS = "FETCH_REVENUE_REPORT_SUCCESS";
export const FETCH_REVENUE_REPORT_FAILED = "FETCH_REVENUE_REPORT_FAILED";

export const fetchRevenueReportProgress = () =>
  action(FETCH_REVENUE_REPORT_PROGRESS);
export const fetchRevenueReportSuccess = (data: IRevenueReportItem[]) =>
  action(FETCH_REVENUE_REPORT_SUCCESS, data);
export const fetchRevenueReportFailed = () =>
  action(FETCH_REVENUE_REPORT_FAILED);

interface IfetchRevenueReport {
  companyOutletUuid: string;
  leadYear?: number;
  leadMonth?: number;
  leadDay?: number;
  type: "year" | "month" | "day";
}
export const fetchRevenueReport =
  (
    params: IfetchRevenueReport,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchRevenueReportProgress());
    try {
      const { companyOutletUuid, leadYear, leadMonth, leadDay, type } = params;

      let url = `/report/get-revenue-report?company_outlet_uuid=${companyOutletUuid}&type=${type}`;

      if (leadYear) {
        url += `&lead_year=${leadYear}`;
      }
      if (leadMonth) {
        url += `&lead_month=${leadMonth}`;
      }
      if (leadDay) {
        url += `&lead_day=${leadDay}`;
      }
      const response = await api.get(url);
      dispatch(fetchRevenueReportSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchRevenueReportFailed());
    }
  };

// Lost Revenue By Reason
export const FETCH_LOST_REVENUE_BY_REASON_PROGRESS =
  "FETCH_LOST_REVENUE_BY_REASON_PROGRESS";
export const FETCH_LOST_REVENUE_BY_REASON_SUCCESS =
  "FETCH_LOST_REVENUE_BY_REASON_SUCCESS";
export const FETCH_LOST_REVENUE_BY_REASON_FAILED =
  "FETCH_LOST_REVENUE_BY_REASON_FAILED";

export const fetchLostRevenueByReasonProgress = () =>
  action(FETCH_LOST_REVENUE_BY_REASON_PROGRESS);
export const fetchLostRevenueByReasonSuccess = (data: ILostRevenueData) =>
  action(FETCH_LOST_REVENUE_BY_REASON_SUCCESS, data);
export const fetchLostRevenueByReasonFailed = () =>
  action(FETCH_LOST_REVENUE_BY_REASON_FAILED);

interface IfetchLostRevenueByReason {
  companyOutletUuid: string;
  leadYear?: number;
  leadMonth?: number;
}

export const fetchLostRevenueByReason =
  (
    params: IfetchLostRevenueByReason,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchLostRevenueByReasonProgress());

    const { companyOutletUuid, leadYear, leadMonth } = params;

    try {
      let url = `/report/get-lost-revenue-by-reason?company_outlet_uuid=${companyOutletUuid}`;

      if (leadYear) {
        url += `&lead_year=${leadYear}`;
      }
      if (leadMonth) {
        url += `&lead_month=${leadMonth}`;
      }

      const response = await api.get(url);
      dispatch(fetchLostRevenueByReasonSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchLostRevenueByReasonFailed());
    }
  };

// Peak Booking Month By Year
export const FETCH_PEAK_BOOKING_PROGRESS = "FETCH_PEAK_BOOKING_PROGRESS";
export const FETCH_PEAK_BOOKING_SUCCESS = "FETCH_PEAK_BOOKING_SUCCESS";
export const FETCH_PEAK_BOOKING_FAILED = "FETCH_PEAK_BOOKING_FAILED";

export const fetchPeakBookingProgress = () =>
  action(FETCH_PEAK_BOOKING_PROGRESS);
export const fetchPeakBookingSuccess = (data: IPeakBookingItem[]) =>
  action(FETCH_PEAK_BOOKING_SUCCESS, data);
export const fetchPeakBookingFailed = () => action(FETCH_PEAK_BOOKING_FAILED);

interface IfetchPeakBooking {
  companyOutletUuid: string;
  leadYear?: number;
  leadMonth?: number;
}

export const fetchPeakBooking =
  (params: IfetchPeakBooking): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchPeakBookingProgress());

    const { companyOutletUuid, leadYear, leadMonth } = params;
    try {
      let url = `/report/get-peak-booking-month-by-year?company_outlet_uuid=${companyOutletUuid}`;

      if (leadYear) {
        url += `&lead_year=${leadYear}`;
      }
      if (leadMonth) {
        url += `&lead_month=${leadMonth}`;
      }

      const response = await api.get(url);
      dispatch(fetchPeakBookingSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchPeakBookingFailed());
    }
  };

// Upsell Contribution
export const FETCH_UPSELL_CONTRIBUTION_PROGRESS =
  "FETCH_UPSELL_CONTRIBUTION_PROGRESS";
export const FETCH_UPSELL_CONTRIBUTION_SUCCESS =
  "FETCH_UPSELL_CONTRIBUTION_SUCCESS";
export const FETCH_UPSELL_CONTRIBUTION_FAILED =
  "FETCH_UPSELL_CONTRIBUTION_FAILED";

export const fetchUpsellContributionProgress = () =>
  action(FETCH_UPSELL_CONTRIBUTION_PROGRESS);
export const fetchUpsellContributionSuccess = (
  data: IUpsellContributionItem[],
) => action(FETCH_UPSELL_CONTRIBUTION_SUCCESS, data);
export const fetchUpsellContributionFailed = () =>
  action(FETCH_UPSELL_CONTRIBUTION_FAILED);

interface IFetchUpsellContribution {
  companyOutletUuid: string;
  leadYear?: number;
  leadMonth?: number;
}

export const fetchUpsellContribution =
  (
    params: IFetchUpsellContribution,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchUpsellContributionProgress());

    const { companyOutletUuid, leadYear, leadMonth } = params;
    try {
      let url = `/report/get-upsell-contribution?company_outlet_uuid=${companyOutletUuid}`;

      if (leadYear) {
        url += `&lead_year=${leadYear}`;
      }
      if (leadMonth) {
        url += `&lead_month=${leadMonth}`;
      }
      const response = await api.get(url);
      dispatch(fetchUpsellContributionSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchUpsellContributionFailed());
    }
  };

// Upsell Contribution Type
export const FETCH_UPSELL_CONTRIBUTION_TYPE_PROGRESS =
  "FETCH_UPSELL_CONTRIBUTION_TYPE_PROGRESS";
export const FETCH_UPSELL_CONTRIBUTION_TYPE_SUCCESS =
  "FETCH_UPSELL_CONTRIBUTION_TYPE_SUCCESS";
export const FETCH_UPSELL_CONTRIBUTION_TYPE_FAILED =
  "FETCH_UPSELL_CONTRIBUTION_TYPE_FAILED";

export const fetchUpsellContributionTypeProgress = () =>
  action(FETCH_UPSELL_CONTRIBUTION_TYPE_PROGRESS);
export const fetchUpsellContributionTypeSuccess = (
  data: IUpsellContributionTypeItem,
) => action(FETCH_UPSELL_CONTRIBUTION_TYPE_SUCCESS, data);
export const fetchUpsellContributionTypeFailed = () =>
  action(FETCH_UPSELL_CONTRIBUTION_TYPE_FAILED);

interface IFetchUpsellContributionType {
  companyOutletUuid: string;
  leadYear?: number;
  leadMonth?: number;
  upsellType: "venue" | "service" | "food";
}

export const fetchUpsellContributionType =
  (
    params: IFetchUpsellContributionType,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchUpsellContributionTypeProgress());
    try {
      const { companyOutletUuid, leadYear, leadMonth, upsellType } = params;

      let url = `/report/get-upsell-contribution-type?company_outlet_uuid=${companyOutletUuid}&upsell_type=${upsellType}`;

      if (leadYear) {
        url += `&lead_year=${leadYear}`;
      }
      if (leadMonth) {
        url += `&lead_month=${leadMonth}`;
      }

      const response = await api.get(url);
      dispatch(fetchUpsellContributionTypeSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchUpsellContributionTypeFailed());
    }
  };

// Owner Insights
export const FETCH_OWNER_INSIGHTS_PROGRESS = "FETCH_OWNER_INSIGHTS_PROGRESS";
export const FETCH_OWNER_INSIGHTS_SUCCESS = "FETCH_OWNER_INSIGHTS_SUCCESS";
export const FETCH_OWNER_INSIGHTS_FAILED = "FETCH_OWNER_INSIGHTS_FAILED";

export const fetchOwnerInsightsProgress = () =>
  action(FETCH_OWNER_INSIGHTS_PROGRESS);
export const fetchOwnerInsightsSuccess = (data: IOwnerInsightItem[]) =>
  action(FETCH_OWNER_INSIGHTS_SUCCESS, data);
export const fetchOwnerInsightsFailed = () =>
  action(FETCH_OWNER_INSIGHTS_FAILED);

interface IFetchOwnerInsights {
  companyOutletUuid: string;
  leadYear?: number;
  leadMonth?: number;
}

export const fetchOwnerInsights =
  (
    params: IFetchOwnerInsights,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchOwnerInsightsProgress());

    const { companyOutletUuid, leadYear, leadMonth } = params;

    try {
      let url = `/report/get-owner-insights?company_outlet_uuid=${companyOutletUuid}`;

      if (leadYear) {
        url += `&lead_year=${leadYear}`;
      }
      if (leadMonth) {
        url += `&lead_month=${leadMonth}`;
      }
      const response = await api.get(url);
      dispatch(fetchOwnerInsightsSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchOwnerInsightsFailed());
    }
  };

// Margin Report
export const FETCH_MARGIN_REPORT_PROGRESS = "FETCH_MARGIN_REPORT_PROGRESS";
export const FETCH_MARGIN_REPORT_SUCCESS = "FETCH_MARGIN_REPORT_SUCCESS";
export const FETCH_MARGIN_REPORT_FAILED = "FETCH_MARGIN_REPORT_FAILED";

export const fetchMarginReportProgress = () =>
  action(FETCH_MARGIN_REPORT_PROGRESS);
export const fetchMarginReportSuccess = (data: IMarginReport[]) =>
  action(FETCH_MARGIN_REPORT_SUCCESS, data);
export const fetchMarginReportFailed = () => action(FETCH_MARGIN_REPORT_FAILED);

interface IFetchMarginReport {
  companyOutletUuid: string;
  leadYear: number;
  leadMonth?: number | "";
}

export const fetchMarginReport =
  (params: IFetchMarginReport): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchMarginReportProgress());
    try {
      const { companyOutletUuid, leadYear, leadMonth } = params;
      let url = `/report/get-margin-report?company_outlet_uuid=${companyOutletUuid}&year=${leadYear}`;

      if (leadMonth) {
        url += `&month=${leadMonth}`;
      }

      const response = await api.get(url);
      dispatch(fetchMarginReportSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchMarginReportFailed());
    }
  };

// Add to your existing actions file

// Owner Report Summary
export const FETCH_OWNER_REPORT_SUMMARY_PROGRESS =
  "FETCH_OWNER_REPORT_SUMMARY_PROGRESS";
export const FETCH_OWNER_REPORT_SUMMARY_SUCCESS =
  "FETCH_OWNER_REPORT_SUMMARY_SUCCESS";
export const FETCH_OWNER_REPORT_SUMMARY_FAILED =
  "FETCH_OWNER_REPORT_SUMMARY_FAILED";

export const fetchOwnerReportSummaryProgress = () =>
  action(FETCH_OWNER_REPORT_SUMMARY_PROGRESS);
export const fetchOwnerReportSummarySuccess = (data: IOwnerReportSummary[]) =>
  action(FETCH_OWNER_REPORT_SUMMARY_SUCCESS, data);
export const fetchOwnerReportSummaryFailed = () =>
  action(FETCH_OWNER_REPORT_SUMMARY_FAILED);

interface IFetchOwnerReportSummary {
  companyOutletUuid: string;
  leadYear?: number;
  leadMonth?: number;
  leadWeek?: number;
}

export const fetchOwnerReportSummary =
  (
    params: IFetchOwnerReportSummary,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchOwnerReportSummaryProgress());
    try {
      const { companyOutletUuid, leadYear, leadMonth, leadWeek } = params;

      let url = `/report/get-owner-report-summary?company_outlet_uuid=${companyOutletUuid}`;

      if (leadYear) {
        url += `&lead_year=${leadYear}`;
      }
      if (leadMonth) {
        url += `&lead_month=${leadMonth}`;
      }
      if (leadWeek) {
        url += `&lead_week=${leadWeek}`;
      }

      const response = await api.get(url);
      dispatch(fetchOwnerReportSummarySuccess(response.data.data));
    } catch (error) {
      dispatch(fetchOwnerReportSummaryFailed());
    }
  };

// Lead Report Summary

// Cancellation List  Report Summary
export const FETCH_CANCELLATION_LIST_REPORT_PROGRESS =
  "FETCH_CANCELLATION_LIST_REPORT_PROGRESS";
export const FETCH_CANCELLATION_LIST_REPORT_SUCCESS =
  "FETCH_CANCELLATION_LIST_REPORT_SUCCESS";
export const FETCH_CANCELLATION_LIST_REPORT_FAILED =
  "FETCH_CANCELLATION_LIST_REPORT_FAILED";

export const fetchCancellationListReportProgress = () =>
  action(FETCH_CANCELLATION_LIST_REPORT_PROGRESS);
export const fetchCancellationListReportSuccess = (data: ILeadReport[]) =>
  action(FETCH_CANCELLATION_LIST_REPORT_SUCCESS, data);
export const fetchCancellationListReportFailed = () =>
  action(FETCH_CANCELLATION_LIST_REPORT_FAILED);

interface IFetchCancellationListReport {
  companyOutletUuid: string;
  actual_status?: string;
  from_date?: string;
  to_date?: string;
}

export const fetchCancellationListReport =
  (
    params: IFetchCancellationListReport,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCancellationListReportProgress());
    try {
      const { companyOutletUuid, actual_status, from_date, to_date } = params;

      let url = `/report/get-cancellation-list?company_outlet_uuid=${companyOutletUuid}`;

      if (actual_status) {
        url += `&actual_status=${actual_status}`;
      }
      if (from_date) {
        url += `&from_date=${from_date}`;
      }
      if (to_date) {
        url += `&to_date=${to_date}`;
      }

      const response = await api.get(url);
      dispatch(fetchCancellationListReportSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchCancellationListReportFailed());
    }
  };

// Cancellation reason List  Report Summary
export const FETCH_CANCELLATION_REASON_LIST_REPORT_PROGRESS =
  "FETCH_CANCELLATION_REASON_LIST_REPORT_PROGRESS";
export const FETCH_CANCELLATION_REASON_LIST_REPORT_SUCCESS =
  "FETCH_CANCELLATION_REASON_LIST_REPORT_SUCCESS";
export const FETCH_CANCELLATION_REASON_LIST_REPORT_FAILED =
  "FETCH_CANCELLATION_REASON_LIST_REPORT_FAILED";

export const fetchCancellationReasonListReportProgress = () =>
  action(FETCH_CANCELLATION_REASON_LIST_REPORT_PROGRESS);
export const fetchCancellationReasonListReportSuccess = (
  data: ICancellationReasonList[],
) => action(FETCH_CANCELLATION_REASON_LIST_REPORT_SUCCESS, data);
export const fetchCancellationReasonListReportFailed = () =>
  action(FETCH_CANCELLATION_REASON_LIST_REPORT_FAILED);

interface IFetchCancellationReasonListReport {
  companyOutletUuid: string;
}

export const fetchCancellationReasonListReport =
  (
    params: IFetchCancellationReasonListReport,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCancellationReasonListReportProgress());
    try {
      const { companyOutletUuid } = params;

      let url = `/report/get-cancellation-reason-list?company_outlet_uuid=${companyOutletUuid}`;

      const response = await api.get(url);
      dispatch(fetchCancellationReasonListReportSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchCancellationReasonListReportFailed());
    }
  };

// Guest Visit List  Report Summary
export const FETCH_GUEST_VISIT_LIST_REPORT_PROGRESS =
  "FETCH_GUEST_VISIT_LIST_REPORT_PROGRESS";
export const FETCH_GUEST_VISIT_LIST_REPORT_SUCCESS =
  "FETCH_GUEST_VISIT_LIST_REPORT_SUCCESS";
export const FETCH_GUEST_VISIT_LIST_REPORT_FAILED =
  "FETCH_GUEST_VISIT_LIST_REPORT_FAILED";

export const fetchGuestVisitListReportProgress = () =>
  action(FETCH_GUEST_VISIT_LIST_REPORT_PROGRESS);
export const fetchGuestVisitListReportSuccess = (data: ILeadReport[]) =>
  action(FETCH_GUEST_VISIT_LIST_REPORT_SUCCESS, data);
export const fetchGuestVisitListReportFailed = () =>
  action(FETCH_GUEST_VISIT_LIST_REPORT_FAILED);

interface IFetchGuestVisitListReport {
  companyOutletUuid: string;
}

export const fetchGuestVisitListReport =
  (
    params: IFetchGuestVisitListReport,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchGuestVisitListReportProgress());
    try {
      const { companyOutletUuid } = params;

      let url = `/report/get-guest-visit-list?company_outlet_uuid=${companyOutletUuid}`;

      const response = await api.get(url);
      dispatch(fetchGuestVisitListReportSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchGuestVisitListReportFailed());
    }
  };

// Ongoing  List  Report Summary
export const FETCH_ONGOING_LIST_REPORT_PROGRESS =
  "FETCH_ONGOING_LIST_REPORT_PROGRESS";
export const FETCH_ONGOING_LIST_REPORT_SUCCESS =
  "FETCH_ONGOING_LIST_REPORT_SUCCESS";
export const FETCH_ONGOING_LIST_REPORT_FAILED =
  "FETCH_ONGOING_LIST_REPORT_FAILED";

export const fetchOngoingListReportProgress = () =>
  action(FETCH_ONGOING_LIST_REPORT_PROGRESS);
export const fetchOngoingListReportSuccess = (
  data: ILeadReport[],
  totalRecords: number,
) => action(FETCH_ONGOING_LIST_REPORT_SUCCESS, { data, totalRecords });
export const fetchOngoingListReportFailed = () =>
  action(FETCH_ONGOING_LIST_REPORT_FAILED);

interface IFetchOngoingListReport {
  companyOutletUuid: string;
  pageNumber?: number;
  rowsInPerPage?: number;
  limit?: number;
}

export const fetchOngoingListReport =
  (
    params: IFetchOngoingListReport,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchOngoingListReportProgress());
    try {
      const {
        companyOutletUuid,
        pageNumber = 1,
        rowsInPerPage = 25,
        limit = 26,
      } = params;

      let url = `/report/get-ongoing-list?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

      const response = await api.get(url);
      const data = response.data.data || [];
      const totalRecords = response.data.totalRecords ?? data.length;
      dispatch(fetchOngoingListReportSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchOngoingListReportFailed());
    }
  };

// Daily Summary Report
export const FETCH_DAILY_SUMMARY_REPORT_PROGRESS =
  "FETCH_DAILY_SUMMARY_REPORT_PROGRESS";
export const FETCH_DAILY_SUMMARY_REPORT_SUCCESS =
  "FETCH_DAILY_SUMMARY_REPORT_SUCCESS";
export const FETCH_DAILY_SUMMARY_REPORT_FAILED =
  "FETCH_DAILY_SUMMARY_REPORT_FAILED";

export const fetchDailySummaryReportProgress = () =>
  action(FETCH_DAILY_SUMMARY_REPORT_PROGRESS);
export const fetchDailySummaryReportSuccess = (
  data: ILeadReport[],
  totalRecords: number,
) => action(FETCH_DAILY_SUMMARY_REPORT_SUCCESS, { data, totalRecords });
export const fetchDailySummaryReportFailed = () =>
  action(FETCH_DAILY_SUMMARY_REPORT_FAILED);

interface IFetchDailySummaryReport {
  companyOutletUuid: string;
  pageNumber?: number;
  rowsInPerPage?: number;
  limit?: number;
}

export const fetchDailySummaryReport =
  (
    params: IFetchDailySummaryReport,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchDailySummaryReportProgress());
    try {
      const {
        companyOutletUuid,
        pageNumber = 1,
        rowsInPerPage = 25,
        limit = 26,
      } = params;

      let url = `/report/get-daily-summary?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

      const response = await api.get(url);
      const data = response.data.data || [];
      const totalRecords = response.data.totalRecords ?? data.length;
      dispatch(fetchDailySummaryReportSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchDailySummaryReportFailed());
    }
  };

// Weekly Summary Report
export const FETCH_WEEKLY_SUMMARY_REPORT_PROGRESS =
  "FETCH_WEEKLY_SUMMARY_REPORT_PROGRESS";
export const FETCH_WEEKLY_SUMMARY_REPORT_SUCCESS =
  "FETCH_WEEKLY_SUMMARY_REPORT_SUCCESS";
export const FETCH_WEEKLY_SUMMARY_REPORT_FAILED =
  "FETCH_WEEKLY_SUMMARY_REPORT_FAILED";

export const fetchWeeklySummaryReportProgress = () =>
  action(FETCH_WEEKLY_SUMMARY_REPORT_PROGRESS);
export const fetchWeeklySummaryReportSuccess = (
  data: ILeadReport[],
  totalRecords: number,
) => action(FETCH_WEEKLY_SUMMARY_REPORT_SUCCESS, { data, totalRecords });
export const fetchDWeeklySummaryReportFailed = () =>
  action(FETCH_WEEKLY_SUMMARY_REPORT_FAILED);

interface IFetchWeeklySummaryReport {
  companyOutletUuid: string;
  pageNumber?: number;
  rowsInPerPage?: number;
  limit?: number;
}

export const fetchWeeklySummaryReport =
  (
    params: IFetchWeeklySummaryReport,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchWeeklySummaryReportProgress());
    try {
      const {
        companyOutletUuid,
        pageNumber = 1,
        rowsInPerPage = 25,
        limit = 26,
      } = params;

      let url = `/report/get-weekly-summary?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

      const response = await api.get(url);
      const data = response.data.data || [];
      const totalRecords = response.data.totalRecords ?? data.length;
      dispatch(fetchWeeklySummaryReportSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchDWeeklySummaryReportFailed());
    }
  };

// get full report preview / pdf
interface IFetcFullReportPdf {
  companyOutletUuid: string;
  type: "pdf" | "preview";
}

export const fetchFullReportPdf =
  (params: IFetcFullReportPdf): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const { companyOutletUuid, type } = params;

      const response = await api.get(
        `/report/get-full-report/${type}?company_outlet_uuid=${companyOutletUuid}`,
        {
          responseType: "arraybuffer",
          validateStatus: (status) => status === 200,
        },
      );

      const contentType = response.headers["content-type"];

      if (!contentType.includes("application/pdf")) {
        throw new Error(`Invalid content type: ${contentType}`);
      }

      dispatch(saveLoaderCompleted());

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "full-report.pdf";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      dispatch(
        showMessage({
          type: "success",
          message:
            response?.data?.message ||
            `Full Report ${type} generated successfully`,
          displayAs: "snackbar",
        }),
      );
    } catch (error: any) {
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "error",
          message: error.response?.data?.message || "An error occurred",
          displayAs: "snackbar",
        }),
      );
    }
  };

// get count of Cancellation - Guest Visit - Lost Revenue
export const FETCH_COUNT_CANCELLATION_GUEST_VISIT_LOST_REVENUE_PROGRESS =
  "FETCH_COUNT_CANCELLATION_GUEST_VISIT_LOST_REVENUE_PROGRESS";
export const FETCH_COUNT_CANCELLATION_GUEST_VISIT_LOST_REVENUE_SUCCESS =
  "FETCH_COUNT_CANCELLATION_GUEST_VISIT_LOST_REVENUE_SUCCESS";
export const FETCH_COUNT_CANCELLATION_GUEST_VISIT_LOST_REVENUE_FAILED =
  "FETCH_COUNT_CANCELLATION_GUEST_VISIT_LOST_REVENUE_FAILED";

export const fetchCountCancellationGuestVisitLostRevenueProgress = () =>
  action(FETCH_COUNT_CANCELLATION_GUEST_VISIT_LOST_REVENUE_PROGRESS);
export const fetchCountCancellationGuestVisitLostRevenueSuccess = (
  data: ICountOfCancellationGuestVisitLostRevenue,
) => action(FETCH_COUNT_CANCELLATION_GUEST_VISIT_LOST_REVENUE_SUCCESS, data);
export const fetchCountCancellationGuestVisitLostRevenueFailed = () =>
  action(FETCH_COUNT_CANCELLATION_GUEST_VISIT_LOST_REVENUE_FAILED);

interface IFetchCountCancellationGuestVisitLostRevenue {
  companyOutletUuid: string;
}

export const fetchCountCancellationGuestVisitLostRevenue =
  (
    params: IFetchCountCancellationGuestVisitLostRevenue,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountCancellationGuestVisitLostRevenueProgress());
    try {
      const { companyOutletUuid } = params;

      let url = `/report/get-count-cancellation-ongoing-lost-revenue?company_outlet_uuid=${companyOutletUuid}`;

      const response = await api.get(url);
      dispatch(
        fetchCountCancellationGuestVisitLostRevenueSuccess(response.data.data),
      );
    } catch (error) {
      dispatch(fetchCountCancellationGuestVisitLostRevenueFailed());
    }
  };

// get count of weekly guest and event summary
export const FETCH_WEEKLY_GUEST_EVENT_SUMMARY_PROGRESS =
  "FETCH_WEEKLY_GUEST_EVENT_SUMMARY_PROGRESS";
export const FETCH_WEEKLY_GUEST_EVENT_SUMMARY_SUCCESS =
  "FETCH_WEEKLY_GUEST_EVENT_SUMMARY_SUCCESS";
export const FETCH_CWEEKLY_GUEST_EVENT_SUMMARY_FAILED =
  "FETCH_CWEEKLY_GUEST_EVENT_SUMMARY_FAILED";

export const fetchWeeklyGuestEventSummaryProgress = () =>
  action(FETCH_WEEKLY_GUEST_EVENT_SUMMARY_PROGRESS);
export const fetchWeeklyGuestEventSummarySuccess = (
  data: ICountOfWeeklyGuestEventSummary,
) => action(FETCH_WEEKLY_GUEST_EVENT_SUMMARY_SUCCESS, data);
export const fetchWeeklyGuestEventSummaryFailed = () =>
  action(FETCH_CWEEKLY_GUEST_EVENT_SUMMARY_FAILED);

interface IFetchWeeklyGuestEventSummary {
  companyOutletUuid: string;
}

export const fetchWeeklyGuestEventSummary =
  (
    params: IFetchWeeklyGuestEventSummary,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchWeeklyGuestEventSummaryProgress());
    try {
      const { companyOutletUuid } = params;

      let url = `/report/get-weekly-guest-and-event-summary?company_outlet_uuid=${companyOutletUuid}`;

      const response = await api.get(url);
      dispatch(fetchWeeklyGuestEventSummarySuccess(response.data.data));
    } catch (error) {
      dispatch(fetchWeeklyGuestEventSummaryFailed());
    }
  };

export const FETCH_COUNT_OF_CANCELLATION_LOST_REVENUE_PROGRESS =
  "FETCH_COUNT_OF_CANCELLATION_LOST_REVENUE_PROGRESS";
export const FETCH_COUNT_OF_CANCELLATION_LOST_REVENUE_SUCCESS =
  "FETCH_COUNT_OF_CANCELLATION_LOST_REVENUE_SUCCESS";
export const FETCH_COUNT_OF_CANCELLATION_LOST_REVENUE_FAILED =
  "FETCH_COUNT_OF_CANCELLATION_LOST_REVENUE_FAILED";

export const fetchCountOfCancellationLostRevenueProgress = () =>
  action(FETCH_COUNT_OF_CANCELLATION_LOST_REVENUE_PROGRESS);
export const fetchCountOfCancellationLostRevenueSuccess = (
  data: ICountOfCancellationLostRevenue,
) => action(FETCH_COUNT_OF_CANCELLATION_LOST_REVENUE_SUCCESS, data);
export const fetchCountOfCancellationLostRevenueFailed = () =>
  action(FETCH_COUNT_OF_CANCELLATION_LOST_REVENUE_FAILED);

interface IFetchCountOfCancellationLostRevenue {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
}

export const fetchCountOfCancellationLostRevenue =
  (
    params: IFetchCountOfCancellationLostRevenue,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfCancellationLostRevenueProgress());
    try {
      const { companyOutletUuid, to_date, from_date } = params;

      let url = `/report/get-count-cancellation-lost-revenue?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}`;

      const response = await api.get(url);
      dispatch(fetchCountOfCancellationLostRevenueSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchCountOfCancellationLostRevenueFailed());
    }
  };

interface IFetchCountOfCancellationReasonList {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
}

export const FETCH_COUNT_OF_CANCELLATION_REASON_LIST_PROGRESS =
  "FETCH_COUNT_OF_CANCELLATION_REASON_LIST_PROGRESS";
export const FETCH_COUNT_OF_CANCELLATION_REASON_LIST_SUCCESS =
  "FETCH_COUNT_OF_CANCELLATION_REASON_LIST_SUCCESS";
export const FETCH_COUNT_OF_CANCELLATION_REASON_LIST_FAILED =
  "FETCH_COUNT_OF_CANCELLATION_REASON_LIST_FAILED";

export const fetchCountOfCancellationReasonListProgress = () =>
  action(FETCH_COUNT_OF_CANCELLATION_REASON_LIST_PROGRESS);
export const fetchCountOfCancellationReasonListSuccess = (
  data: ICountOfCancellationReasonList[],
) => action(FETCH_COUNT_OF_CANCELLATION_REASON_LIST_SUCCESS, data);
export const fetchCountOfCancellationReasonListFailed = () =>
  action(FETCH_COUNT_OF_CANCELLATION_REASON_LIST_FAILED);

interface IFetchCountOfCancellationReasonList {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
}

export const fetchCountOfCancellationReasonList =
  (
    params: IFetchCountOfCancellationReasonList,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfCancellationReasonListProgress());
    try {
      const { companyOutletUuid, to_date, from_date } = params;

      let url = `/report/get-cancellation-reason-list?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}`;

      const response = await api.get(url);
      dispatch(fetchCountOfCancellationReasonListSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchCountOfCancellationReasonListFailed());
    }
  };

// count of cancellation list
export const FETCH_COUNT_OF_CANCELLATION_LIST_PROGRESS =
  "FETCH_COUNT_OF_CANCELLATION_LIST_PROGRESS";
export const FETCH_COUNT_OF_CANCELLATION_LIST_SUCCESS =
  "FETCH_COUNT_OF_CANCELLATION_LIST_SUCCESS";
export const FETCH_COUNT_OF_CANCELLATION_LIST_FAILED =
  "FETCH_COUNT_OF_CANCELLATION_LIST_FAILED";

export const fetchCountOfCancellationListProgress = () =>
  action(FETCH_COUNT_OF_CANCELLATION_LIST_PROGRESS);
export const fetchCountOfCancellationListSuccess = (
  data: ICountOfCancellationList[],
  totalRecords: number,
) => action(FETCH_COUNT_OF_CANCELLATION_LIST_SUCCESS, { data, totalRecords });
export const fetchCountOfCancellationListFailed = () =>
  action(FETCH_COUNT_OF_CANCELLATION_LIST_FAILED);

interface IFetchCountOfCancellationList {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
  pageNumber: number;
  rowsInPerPage: number;
  limit: number;
}

export const fetchCountOfCancellationList =
  (
    params: IFetchCountOfCancellationList,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfCancellationListProgress());
    try {
      const {
        companyOutletUuid,
        to_date,
        from_date,
        pageNumber,
        rowsInPerPage,
        limit,
      } = params;

      const url = `/report/get-cancellation-list?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

      const response = await api.get(url);
      const data = response.data.data || [];
      const totalRecords = response.data.totalRecords ?? data.length;
      dispatch(fetchCountOfCancellationListSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchCountOfCancellationListFailed());
    }
  };

// Todays Cancellation List
export const FETCH_TODAYS_CANCELLATION_LIST_PROGRESS =
  "FETCH_TODAYS_CANCELLATION_LIST_PROGRESS";
export const FETCH_TODAYS_CANCELLATION_LIST_SUCCESS =
  "FETCH_TODAYS_CANCELLATION_LIST_SUCCESS";
export const FETCH_TODAYS_CANCELLATION_LIST_FAILED =
  "FETCH_TODAYS_CANCELLATION_LIST_FAILED";

export const fetchTodaysCancellationListProgress = () =>
  action(FETCH_TODAYS_CANCELLATION_LIST_PROGRESS);
export const fetchTodaysCancellationListSuccess = (
  data: ICountOfCancellationList[],
  totalRecords: number,
) => action(FETCH_TODAYS_CANCELLATION_LIST_SUCCESS, { data, totalRecords });
export const fetchTodaysCancellationListFailed = () =>
  action(FETCH_TODAYS_CANCELLATION_LIST_FAILED);

interface IFetchTodaysCancellationList {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
  pageNumber: number;
  rowsInPerPage: number;
  limit: number;
}

export const fetchTodaysCancellationList =
  (
    params: IFetchTodaysCancellationList,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchTodaysCancellationListProgress());
    try {
      const {
        companyOutletUuid,
        to_date,
        from_date,
        pageNumber,
        rowsInPerPage,
        limit,
      } = params;

      const url = `/report/get-cancellation-list?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

      const response = await api.get(url);
      const data = response.data.data || [];
      const totalRecords = response.data.totalRecords ?? data.length;
      dispatch(fetchTodaysCancellationListSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchTodaysCancellationListFailed());
    }
  };

export const FETCH_TODAY_FOLLOWUPS_LIST_PROGRESS =
  "FETCH_TODAY_FOLLOWUPS_LIST_PROGRESS";
export const FETCH_TODAY_FOLLOWUPS_LIST_SUCCESS =
  "FETCH_TODAY_FOLLOWUPS_LIST_SUCCESS";
export const FETCH_TODAY_FOLLOWUPS_LIST_FAILED =
  "FETCH_TODAY_FOLLOWUPS_LIST_FAILED";

export const fetchTodayFollowupsListProgress = () =>
  action(FETCH_TODAY_FOLLOWUPS_LIST_PROGRESS);
export const fetchTodayFollowupsListSuccess = (data: ITodayFollowupItem[]) =>
  action(FETCH_TODAY_FOLLOWUPS_LIST_SUCCESS, data);
export const fetchTodayFollowupsListFailed = () =>
  action(FETCH_TODAY_FOLLOWUPS_LIST_FAILED);

interface IFetchTodayFollowupsList {
  companyOutletUuid: string;
}

export const fetchTodayFollowupsList =
  (
    params: IFetchTodayFollowupsList,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchTodayFollowupsListProgress());
    try {
      const { companyOutletUuid } = params;
      const url = `/report/get-today-followups?company_outlet_uuid=${companyOutletUuid}`;
      const response = await api.get(url);
      const data: ITodayFollowupItem[] = response.data.data || [];
      dispatch(fetchTodayFollowupsListSuccess(data));
    } catch (error) {
      dispatch(fetchTodayFollowupsListFailed());
    }
  };

// count of Guest Visit List

export const FETCH_COUNT_OF_GUEST_VISIT_LIST_PROGRESS =
  "FETCH_COUNT_OF_GUEST_VISIT_LIST_PROGRESS";
export const FETCH_COUNT_OF_GUEST_VISIT_LIST_SUCCESS =
  "FETCH_COUNT_OF_GUEST_VISIT_LIST_SUCCESS";
export const FETCH_COUNT_OF_GUEST_VISIT_LIST_FAILED =
  "FETCH_COUNT_OF_GUEST_VISIT_LIST_FAILED";

export const fetchCountOfGuestVisitListProgress = () =>
  action(FETCH_COUNT_OF_GUEST_VISIT_LIST_PROGRESS);
export const fetchCountOfGuestVisitListSuccess = (
  data: ICountOfGuestVisitList[],
  totalRecords: number,
) => action(FETCH_COUNT_OF_GUEST_VISIT_LIST_SUCCESS, { data, totalRecords });
export const fetchCountOfGuestVisitListFailed = () =>
  action(FETCH_COUNT_OF_GUEST_VISIT_LIST_FAILED);

interface IFetchCountOfGuestVisitList {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
  pageNumber: number;
  rowsInPerPage: number;
  limit: number;
}

export const fetchCountOfGuestVisitList =
  (
    params: IFetchCountOfGuestVisitList,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfGuestVisitListProgress());
    try {
      const {
        companyOutletUuid,
        to_date,
        from_date,
        pageNumber,
        rowsInPerPage,
        limit,
      } = params;

      const url = `/report/get-guest-visit-list?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

      const response = await api.get(url);
      const data = response.data.data || [];
      const totalRecords = response.data.totalRecords ?? data.length;
      dispatch(fetchCountOfGuestVisitListSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchCountOfGuestVisitListFailed());
    }
  };

// count of guest visit chips

export const FETCH_COUNT_OF_GUEST_VISIT_CHIPS_PROGRESS =
  "FETCH_COUNT_OF_GUEST_VISIT_CHIPS_PROGRESS";
export const FETCH_COUNT_OF_GUEST_VISIT_CHIPS_SUCCESS =
  "FETCH_COUNT_OF_GUEST_VISIT_CHIPS_SUCCESS";
export const FETCH_COUNT_OF_GUEST_VISIT_CHIPS_FAILED =
  "FETCH_COUNT_OF_GUEST_VISIT_CHIPS_FAILED";

export const fetchCountOfGuestVisitChipsProgress = () =>
  action(FETCH_COUNT_OF_GUEST_VISIT_CHIPS_PROGRESS);
export const fetchCountOfGuestVisitChipsSuccess = (
  data: ICountOfGuestVisitChips,
) => action(FETCH_COUNT_OF_GUEST_VISIT_CHIPS_SUCCESS, data);
export const fetchCountOfGuestVisitChipsFailed = () =>
  action(FETCH_COUNT_OF_GUEST_VISIT_CHIPS_FAILED);

interface IFetchCountOfGuestVisitChips {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
}

export const fetchCountOfGuestVisitChips =
  (
    params: IFetchCountOfGuestVisitChips,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfGuestVisitChipsProgress());
    try {
      const { companyOutletUuid, to_date, from_date } = params;

      let url = `/report/get-guest-visit-count?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}`;

      const response = await api.get(url);
      dispatch(fetchCountOfGuestVisitChipsSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchCountOfGuestVisitChipsFailed());
    }
  };

// employee performance report

export const FETCH_EMPLOYEE_PERFORMANCE_REPORT_PROGRESS =
  "FETCH_EMPLOYEE_PERFORMANCE_REPORT_PROGRESS";
export const FETCH_EMPLOYEE_PERFORMANCE_REPORT_SUCCESS =
  "FETCH_EMPLOYEE_PERFORMANCE_REPORT_SUCCESS";
export const FETCH_EMPLOYEE_PERFORMANCE_REPORT_FAILED =
  "FETCH_EMPLOYEE_PERFORMANCE_REPORT_FAILED";

export const fetchEmployeePerformanceReportProgress = () =>
  action(FETCH_EMPLOYEE_PERFORMANCE_REPORT_PROGRESS);
export const fetchEmployeePerformanceReportSuccess = (
  data: IEmployeePerformanceReport[],
  totalRecords: number,
) => action(FETCH_EMPLOYEE_PERFORMANCE_REPORT_SUCCESS, { data, totalRecords });
export const fetchEmployeePerformanceReportFailed = () =>
  action(FETCH_EMPLOYEE_PERFORMANCE_REPORT_FAILED);

interface IFetchEmployeePerformanceReport {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
  pageNumber: number;
  rowsInPerPage: number;
  limit: number;
}

export const fetchEmployeePerformanceReport =
  (
    params: IFetchEmployeePerformanceReport,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchEmployeePerformanceReportProgress());
    try {
      const {
        companyOutletUuid,
        to_date,
        from_date,
        pageNumber,
        rowsInPerPage,
        limit,
      } = params;

      const url = `/report/get-employee-performance-report?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

      const response = await api.get(url);
      const data = response.data.data || [];
      const totalRecords = response.data.totalRecords ?? data.length;
      dispatch(fetchEmployeePerformanceReportSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchEmployeePerformanceReportFailed());
    }
  };

// Count of Ongoing More Than 15 Days Chips
export const FETCH_COUNT_OF_ONGOING_MORE_THAN_15_DAYS_CHIPS_PROGRESS =
  "FETCH_COUNT_OF_ONGOING_MORE_THAN_15_DAYS_CHIPS_PROGRESS";
export const FETCH_COUNT_OF_ONGOING_MORE_THAN_15_DAYS_CHIPS_SUCCESS =
  "FETCH_COUNT_OF_ONGOING_MORE_THAN_15_DAYS_CHIPS_SUCCESS";
export const FETCH_COUNT_OF_ONGOING_MORE_THAN_15_DAYS_CHIPS_FAILED =
  "FETCH_COUNT_OF_ONGOING_MORE_THAN_15_DAYS_CHIPS_FAILED";

export const fetchCountOfOngoingMoreThan15DaysChipsProgress = () =>
  action(FETCH_COUNT_OF_ONGOING_MORE_THAN_15_DAYS_CHIPS_PROGRESS);
export const fetchCountOfOngoingMoreThan15DaysChipsSuccess = (
  data: ICountOfOngoingMoreThan15DaysChips,
) => action(FETCH_COUNT_OF_ONGOING_MORE_THAN_15_DAYS_CHIPS_SUCCESS, data);
export const fetchCountOfOngoingMoreThan15DaysChipsFailed = () =>
  action(FETCH_COUNT_OF_ONGOING_MORE_THAN_15_DAYS_CHIPS_FAILED);

interface IFetchCountOfOngoingMoreThan15DaysChips {
  companyOutletUuid: string;
}

export const fetchCountOfOngoingMoreThan15DaysChips =
  (
    params: IFetchCountOfOngoingMoreThan15DaysChips,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfOngoingMoreThan15DaysChipsProgress());
    try {
      const { companyOutletUuid } = params;
      let url = `/report/get-ongoing-count?company_outlet_uuid=${companyOutletUuid}`;
      const response = await api.get(url);
      dispatch(
        fetchCountOfOngoingMoreThan15DaysChipsSuccess(response.data.data),
      );
    } catch (error) {
      dispatch(fetchCountOfOngoingMoreThan15DaysChipsFailed());
    }
  };

// Count of Daily Summary Chips
export const FETCH_COUNT_OF_DAILY_SUMMARY_CHIPS_PROGRESS =
  "FETCH_COUNT_OF_DAILY_SUMMARY_CHIPS_PROGRESS";
export const FETCH_COUNT_OF_DAILY_SUMMARY_CHIPS_SUCCESS =
  "FETCH_COUNT_OF_DAILY_SUMMARY_CHIPS_SUCCESS";
export const FETCH_COUNT_OF_DAILY_SUMMARY_CHIPS_FAILED =
  "FETCH_COUNT_OF_DAILY_SUMMARY_CHIPS_FAILED";

export const fetchCountOfDailySummaryChipsProgress = () =>
  action(FETCH_COUNT_OF_DAILY_SUMMARY_CHIPS_PROGRESS);
export const fetchCountOfDailySummaryChipsSuccess = (
  data: ICountOfDailySummaryChips,
) => action(FETCH_COUNT_OF_DAILY_SUMMARY_CHIPS_SUCCESS, data);
export const fetchCountOfDailySummaryChipsFailed = () =>
  action(FETCH_COUNT_OF_DAILY_SUMMARY_CHIPS_FAILED);

interface IFetchCountOfDailySummaryChips {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
}

export const fetchCountOfDailySummaryChips =
  (
    params: IFetchCountOfDailySummaryChips,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfDailySummaryChipsProgress());
    try {
      const { companyOutletUuid, to_date, from_date } = params;
      let url = `/report/get-daily-summary-count?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}`;
      const response = await api.get(url);
      dispatch(fetchCountOfDailySummaryChipsSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchCountOfDailySummaryChipsFailed());
    }
  };

// Count of Weekly Summary Chips
export const FETCH_COUNT_OF_WEEKLY_SUMMARY_CHIPS_PROGRESS =
  "FETCH_COUNT_OF_WEEKLY_SUMMARY_CHIPS_PROGRESS";
export const FETCH_COUNT_OF_WEEKLY_SUMMARY_CHIPS_SUCCESS =
  "FETCH_COUNT_OF_WEEKLY_SUMMARY_CHIPS_SUCCESS";
export const FETCH_COUNT_OF_WEEKLY_SUMMARY_CHIPS_FAILED =
  "FETCH_COUNT_OF_WEEKLY_SUMMARY_CHIPS_FAILED";

export const fetchCountOfWeeklySummaryChipsProgress = () =>
  action(FETCH_COUNT_OF_WEEKLY_SUMMARY_CHIPS_PROGRESS);
export const fetchCountOfWeeklySummaryChipsSuccess = (
  data: ICountOfWeeklySummaryChips,
) => action(FETCH_COUNT_OF_WEEKLY_SUMMARY_CHIPS_SUCCESS, data);
export const fetchCountOfWeeklySummaryChipsFailed = () =>
  action(FETCH_COUNT_OF_WEEKLY_SUMMARY_CHIPS_FAILED);

interface IFetchCountOfWeeklySummaryChips {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
}

export const fetchCountOfWeeklySummaryChips =
  (
    params: IFetchCountOfWeeklySummaryChips,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfWeeklySummaryChipsProgress());
    try {
      const { companyOutletUuid, to_date, from_date } = params;
      let url = `/report/get-weekly-summary-count?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}`;
      const response = await api.get(url);
      dispatch(fetchCountOfWeeklySummaryChipsSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchCountOfWeeklySummaryChipsFailed());
    }
  };

// Count of Employee Performance Report Chips
export const FETCH_COUNT_OF_EMPLOYEE_PERFORMANCE_REPORT_CHIPS_PROGRESS =
  "FETCH_COUNT_OF_EMPLOYEE_PERFORMANCE_REPORT_CHIPS_PROGRESS";
export const FETCH_COUNT_OF_EMPLOYEE_PERFORMANCE_REPORT_CHIPS_SUCCESS =
  "FETCH_COUNT_OF_EMPLOYEE_PERFORMANCE_REPORT_CHIPS_SUCCESS";
export const FETCH_COUNT_OF_EMPLOYEE_PERFORMANCE_REPORT_CHIPS_FAILED =
  "FETCH_COUNT_OF_EMPLOYEE_PERFORMANCE_REPORT_CHIPS_FAILED";

export const fetchCountOfEmployeePerformanceReportChipsProgress = () =>
  action(FETCH_COUNT_OF_EMPLOYEE_PERFORMANCE_REPORT_CHIPS_PROGRESS);
export const fetchCountOfEmployeePerformanceReportChipsSuccess = (
  data: ICountOfEmployeePerformanceReportChips,
) => action(FETCH_COUNT_OF_EMPLOYEE_PERFORMANCE_REPORT_CHIPS_SUCCESS, data);
export const fetchCountOfEmployeePerformanceReportChipsFailed = () =>
  action(FETCH_COUNT_OF_EMPLOYEE_PERFORMANCE_REPORT_CHIPS_FAILED);

interface IFetchCountOfEmployeePerformanceReportChips {
  companyOutletUuid: string;
  to_date: string;
  from_date: string;
}

export const fetchCountOfEmployeePerformanceReportChips =
  (
    params: IFetchCountOfEmployeePerformanceReportChips,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfEmployeePerformanceReportChipsProgress());
    try {
      const { companyOutletUuid, to_date, from_date } = params;
      let url = `/report/get-employee-analytics-report?company_outlet_uuid=${companyOutletUuid}&to_date=${to_date}&from_date=${from_date}`;
      const response = await api.get(url);
      dispatch(
        fetchCountOfEmployeePerformanceReportChipsSuccess(response.data.data),
      );
    } catch (error) {
      dispatch(fetchCountOfEmployeePerformanceReportChipsFailed());
    }
  };

// Venue Performance Overview
export const FETCH_VENUE_PERFORMANCE_OVERVIEW_PROGRESS =
  "FETCH_VENUE_PERFORMANCE_OVERVIEW_PROGRESS";
export const FETCH_VENUE_PERFORMANCE_OVERVIEW_SUCCESS =
  "FETCH_VENUE_PERFORMANCE_OVERVIEW_SUCCESS";
export const FETCH_VENUE_PERFORMANCE_OVERVIEW_FAILED =
  "FETCH_VENUE_PERFORMANCE_OVERVIEW_FAILED";

export const fetchVenuePerformanceOverviewProgress = () =>
  action(FETCH_VENUE_PERFORMANCE_OVERVIEW_PROGRESS);
export const fetchVenuePerformanceOverviewSuccess = (
  data: IVenuePerformanceOverview[],
  totalRecords: number,
) => action(FETCH_VENUE_PERFORMANCE_OVERVIEW_SUCCESS, { data, totalRecords });
export const fetchVenuePerformanceOverviewFailed = () =>
  action(FETCH_VENUE_PERFORMANCE_OVERVIEW_FAILED);

interface IFetchVenuePerformanceOverview {
  companyOutletUuid: string;
  from_date: string;
  to_date: string;
  pageNumber: number;
  rowsInPerPage: number;
  limit: number;
}

export const fetchVenuePerformanceOverview =
  (
    params: IFetchVenuePerformanceOverview,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchVenuePerformanceOverviewProgress());
    try {
      const {
        companyOutletUuid,
        from_date,
        to_date,
        pageNumber,
        rowsInPerPage,
        limit,
      } = params;

      const url = `/report/get-venue-performance-overview?company_outlet_uuid=${companyOutletUuid}&from_date=${from_date}&to_date=${to_date}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

      const response = await api.get(url);
      const data = response.data.data || [];
      const totalRecords = response.data.totalRecords ?? data.length;
      dispatch(fetchVenuePerformanceOverviewSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchVenuePerformanceOverviewFailed());
    }
  };

// Count of Ongoing Aging Chips
export const FETCH_COUNT_OF_ONGOING_AGING_PROGRESS =
  "FETCH_COUNT_OF_ONGOING_AGING_PROGRESS";
export const FETCH_COUNT_OF_ONGOING_AGING_SUCCESS =
  "FETCH_COUNT_OF_ONGOING_AGING_SUCCESS";
export const FETCH_COUNT_OF_ONGOING_AGING_FAILED =
  "FETCH_COUNT_OF_ONGOING_AGING_FAILED";

export const fetchCountOfOngoingAgingProgress = () =>
  action(FETCH_COUNT_OF_ONGOING_AGING_PROGRESS);
export const fetchCountOfOngoingAgingSuccess = (data: ICountOfOngoingAging) =>
  action(FETCH_COUNT_OF_ONGOING_AGING_SUCCESS, data);
export const fetchCountOfOngoingAgingFailed = () =>
  action(FETCH_COUNT_OF_ONGOING_AGING_FAILED);

interface IFetchCountOfOngoingAging {
  companyOutletUuid: string;
}

export const fetchCountOfOngoingAging =
  (
    params: IFetchCountOfOngoingAging,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchCountOfOngoingAgingProgress());
    try {
      const { companyOutletUuid } = params;
      let url = `/report/get-ongoing-aging-summary?company_outlet_uuid=${companyOutletUuid}`;
      const response = await api.get(url);
      dispatch(fetchCountOfOngoingAgingSuccess(response.data.data));
    } catch (error) {
      dispatch(fetchCountOfOngoingAgingFailed());
    }
  };

// Event Summary (for Margin Report KPI cards)
export const FETCH_EVENT_SUMMARY_PROGRESS = "FETCH_EVENT_SUMMARY_PROGRESS";
export const FETCH_EVENT_SUMMARY_SUCCESS = "FETCH_EVENT_SUMMARY_SUCCESS";
export const FETCH_EVENT_SUMMARY_FAILED = "FETCH_EVENT_SUMMARY_FAILED";

export const fetchEventSummaryProgress = () =>
  action(FETCH_EVENT_SUMMARY_PROGRESS);
export const fetchEventSummarySuccess = (data: IEventSummary) =>
  action(FETCH_EVENT_SUMMARY_SUCCESS, data);
export const fetchEventSummaryFailed = () => action(FETCH_EVENT_SUMMARY_FAILED);

interface IFetchEventSummary {
  companyOutletUuid: string;
  from_date: string;
  to_date: string;
}

export const fetchEventSummary =
  (params: IFetchEventSummary): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchEventSummaryProgress());
    try {
      const { companyOutletUuid, from_date, to_date } = params;
      const url = `/report/get-event-summary?company_outlet_uuid=${companyOutletUuid}&from_date=${from_date}&to_date=${to_date}`;
      const response = await api.get(url);
      dispatch(fetchEventSummarySuccess(response.data.data));
    } catch (error) {
      dispatch(fetchEventSummaryFailed());
    }
  };

// Event Type Report (for Margin Report table)
export const FETCH_EVENT_TYPE_REPORT_PROGRESS =
  "FETCH_EVENT_TYPE_REPORT_PROGRESS";
export const FETCH_EVENT_TYPE_REPORT_SUCCESS =
  "FETCH_EVENT_TYPE_REPORT_SUCCESS";
export const FETCH_EVENT_TYPE_REPORT_FAILED = "FETCH_EVENT_TYPE_REPORT_FAILED";

export const fetchEventTypeReportProgress = () =>
  action(FETCH_EVENT_TYPE_REPORT_PROGRESS);
export const fetchEventTypeReportSuccess = (
  data: IEventTypeReportItem[],
  totalRecords: number,
) => action(FETCH_EVENT_TYPE_REPORT_SUCCESS, { data, totalRecords });
export const fetchEventTypeReportFailed = () =>
  action(FETCH_EVENT_TYPE_REPORT_FAILED);

interface IFetchEventTypeReport {
  companyOutletUuid: string;
  from_date: string;
  to_date: string;
  pageNumber: number;
  rowsInPerPage: number;
  limit: number;
}

export const fetchEventTypeReport =
  (
    params: IFetchEventTypeReport,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchEventTypeReportProgress());
    try {
      const {
        companyOutletUuid,
        from_date,
        to_date,
        pageNumber,
        rowsInPerPage,
        limit,
      } = params;
      const url = `/report/get-event-type-report?company_outlet_uuid=${companyOutletUuid}&from_date=${from_date}&to_date=${to_date}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;
      const response = await api.get(url);
      const data = response.data.data || [];
      const totalRecords = response.data.totalRecords ?? data.length;
      dispatch(fetchEventTypeReportSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchEventTypeReportFailed());
    }
  };

// Room report (booking heatmap)
export const FETCH_ROOM_REPORT_PROGRESS = "FETCH_ROOM_REPORT_PROGRESS";
export const FETCH_ROOM_REPORT_SUCCESS = "FETCH_ROOM_REPORT_SUCCESS";
export const FETCH_ROOM_REPORT_FAILED = "FETCH_ROOM_REPORT_FAILED";

export const fetchRoomReportProgress = () => action(FETCH_ROOM_REPORT_PROGRESS);
export const fetchRoomReportSuccess = (data: IRoomReportData) =>
  action(FETCH_ROOM_REPORT_SUCCESS, data);
export const fetchRoomReportFailed = () => action(FETCH_ROOM_REPORT_FAILED);

interface IFetchRoomReport {
  companyOutletUuid: string;
  from_date: string;
  to_date: string;
}

export const fetchRoomReport =
  (params: IFetchRoomReport): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchRoomReportProgress());
    try {
      const { companyOutletUuid, from_date, to_date } = params;
      const url = `/report/get-room-report?company_outlet_uuid=${companyOutletUuid}&from_date=${from_date}&to_date=${to_date}`;
      const response = await api.get(url);
      const raw = response.data?.data;
      const data: IRoomReportData =
        raw && typeof raw === "object" ? raw : {};
      dispatch(fetchRoomReportSuccess(data));
    } catch {
      dispatch(fetchRoomReportFailed());
    }
  };
